import './styles.scss';

import { ChangeEvent, FocusEvent, ForwardedRef, MouseEvent, ReactElement, forwardRef, useEffect, useRef, useState } from 'react';
import { cleanTimeRegExp, formatTime } from '@/common/TimeInput/utils';

import PopoverInput from '@/common/PopoverInput';
import { TIME_FORMAT } from '@/constants';
import TimeInputPopover from '@/common/TimeInput/TimeInputPopover';
import { TimeInputTypes } from '@/common/TimeInput/types';
import { getClasses } from '@/utils';
import useForwardRef from '@/hooks/useForwardRef';

const TimeInput = forwardRef<TimeInputTypes.RefType, TimeInputTypes.Props>(
  (
    {
      format = TIME_FORMAT,
      showHours = true,
      showMinutes = true,
      showSeconds = false,
      disableHours = false,
      disableMinutes = false,
      disableSeconds = false,
      ...props
    }: TimeInputTypes.Props,
    ref: ForwardedRef<TimeInputTypes.RefType>
  ): ReactElement => {
    // Input Ref/State
    const inputRef = useForwardRef(ref);
    const [value, setValue] = useState<string>(() => formatTime(format)(props.value || ''));
    const isFocusedRef = useRef(false);

    // Sync external value changes only when the input is not focused (e.g. when BE data
    // loads after mount, or when the drawer opens for a new trip without unmounting).
    // Skipping the sync while focused prevents overwriting the user's in-progress input.
    // Always format to HH:mm (or the configured format) so seconds/full datetimes are stripped.
    useEffect(() => {
      if (!isFocusedRef.current) {
        const formatted = formatTime(format)(props.value || '');
        setValue((prev) => (prev !== formatted ? formatted : prev));
      }
    }, [props.value, format]);

    // Handle Change
    const onChange = (
      newValue: string,
      focus: boolean = false,
      event?: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement> | MouseEvent<HTMLInputElement>
    ): string => {
      setValue(newValue);
      props.onChange?.(newValue, (event || { target: inputRef.current }) as ChangeEvent<HTMLInputElement>);
      if (focus) inputRef.current?.focus();
      return newValue;
    };

    return (
      <PopoverInput
        placeholder={props.placeholder || format.replace(/[a-zA-Z]/g, '-')}
        onFormat={formatTime(format)}
        {...props}
        type="format"
        value={value}
        className={getClasses('TimeInput', 'Common', props.className)}
        onFocus={(value, event) => {
          isFocusedRef.current = true;
          props.onFocus?.(value, event);
        }}
        onChange={(value: string, event: ChangeEvent<HTMLInputElement>): void => {
          value = value.replace(cleanTimeRegExp, '');
          event.target.value = onChange(value, false, event);
        }}
        onBlur={(value: string, event: FocusEvent<HTMLInputElement>): void => {
          isFocusedRef.current = false;
          onChange(value, false, event);
          props.onBlur?.(value, event);
        }}
        ref={inputRef}
      >
        <TimeInputPopover
          value={value}
          format={format}
          target={inputRef}
          onChange={onChange}
          showHours={showHours}
          showMinutes={showMinutes}
          showSeconds={showSeconds}
          disableHours={disableHours}
          disableMinutes={disableMinutes}
          disableSeconds={disableSeconds}
        />
      </PopoverInput>
    );
  }
);
TimeInput.displayName = 'TimeInput';

export default TimeInput;
